const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// 401/403エラー時のコールバック(認証エラー処理)
let onUnauthorizedCallback: (() => void) | null = null;

export const setUnauthorizedCallback = (callback: () => void) => {
  onUnauthorizedCallback = callback;
};

// トークンリフレッシュ中フラグ(複数リクエストが同時にリフレッシュを試みるのを防ぐ)
let isRefreshing = false;
let refreshPromise: Promise<string | null> | null = null;

// トークンをリフレッシュする内部関数
const refreshAccessToken = async (): Promise<string | null> => {
  if (isRefreshing) {
    // 既にリフレッシュ中の場合は、その Promise を返す
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      console.log('Attempting to refresh access token...');
      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include', // Cookieを送信
      });

      if (!response.ok) {
        console.error('Token refresh request failed:', response.status);
        return null;
      }

      const result = await response.json();
      const expiresAt = Date.now() + result.expiresIn;

      // 新しいトークンを保存
      localStorage.setItem('authToken', result.accessToken);
      localStorage.setItem('tokenExpiresAt', expiresAt.toString());

      console.log('Access token refreshed successfully');
      return result.accessToken;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// ログアウト処理を実行する内部関数
const performLogout = async (): Promise<void> => {
  try {
    // ログアウトAPIを呼び出し
    await fetch(`${API_URL}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    });
  } catch (error) {
    console.error('Logout API call failed:', error);
  } finally {
    // トークンを破棄
    localStorage.removeItem('authToken');
    localStorage.removeItem('tokenExpiresAt');
    // ログアウトコールバックを実行
    if (onUnauthorizedCallback) {
      onUnauthorizedCallback();
    }
  }
};

export const fetchData = async (
  endpoint: string,
  options?: RequestInit,
  isRetry: boolean = false
): Promise<Response> => {
  const useMock = process.env.REACT_APP_USE_MOCK === 'true';

  // 認証関連のエンドポイントかどうかを判定
  const isAuthEndpoint = endpoint.startsWith('auth/');

  // トークンを取得してヘッダーに追加
  const token = localStorage.getItem('authToken');

  // ヘッダーを安全にマージ
  const headers: HeadersInit = new Headers(options?.headers);

  // 認証エンドポイント以外でトークンがある場合、Authorizationヘッダーを追加
  if (token && !isAuthEndpoint) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const requestOptions: RequestInit = {
    ...options,
    headers,
    credentials: options?.credentials || 'include', // デフォルトでCookieを送受信
  };

  const url = `${API_URL}/${endpoint}`;
  const response = await fetch(url, requestOptions);

  if (useMock) {
    console.log('Using mock API');
  } else {
    console.log('Using real API:', url);
  }

  // 認証エンドポイントの場合はリフレッシュ処理をスキップ
  if (isAuthEndpoint) {
    return response;
  }

  // 401エラーの場合、トークンをリフレッシュして再試行
  if (response.status === 401 && !isRetry) {
    console.log('401 Unauthorized - Attempting to refresh token');

    const newToken = await refreshAccessToken();

    if (newToken) {
      // トークンリフレッシュ成功、同じリクエストを再試行
      console.log('Token refreshed successfully, retrying request');
      return fetchData(endpoint, options, true);
    } else {
      // トークンリフレッシュ失敗、ログアウト
      console.log('Token refresh failed, logging out');
      await performLogout();
      return response;
    }
  }

  // 2回目の401エラーまたは403エラーの場合、ログアウト
  if ((response.status === 401 && isRetry) || response.status === 403) {
    console.log(`${response.status} error on retry or forbidden - Logging out`);
    await performLogout();
  }

  return response;
};
